<script lang="ts">
  import { goto } from '$app/navigation';
  import { routes, withPreviewMode } from '$lib/routes';
  import { surveyStore } from '$lib/stores/surveyStore';
  import type { InterpretedSurvey } from '$lib/utils/surveyInterpreter';
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();
  import { browser } from '$app/environment';
  import { fade } from 'svelte/transition';
  import { queryParamService } from '$lib/services/QueryParamService';
  import translations from '$lib/translations/survey-player.yaml';
  import { getScreenDisplayTitle, deduplicateScreens } from '$lib/utils/questionUtils';
  
  // Define the screen type based on InterpretedSurvey
  type SurveyScreen = InterpretedSurvey['screens'][number];
  
  // Props
  export let survey: InterpretedSurvey | undefined = undefined;
  export let currentIndex: number = 0;
  export let forceVisible: boolean = false;
  export let routePrefix: string = '';
  export let filteredQuestionIds: string[] = []; // When provided, only show questions with these IDs
  export let hidden = false;

  // Get language from survey data - wait for it to be available
  $: language = survey?.config?.language || (browser && (window as any).__SURVEY_DATA__?.config?.language) || null;
  
  $: currentTranslations = language ? (translations[language] || translations.en_us) : translations.en_us;
  
  // Track the last known current index to detect changes
  let lastKnownIndex = currentIndex;
  
  // Local state
  let isOpen = false;
  let previewMode = forceVisible;
  
  // For stats routes, completely bypass preview mode checks
  $: showNavigator = forceVisible || previewMode;
  let unsubscribe: () => void;
  let searchQuery = '';
  let isSmallScreen = false;
  let selectedResultIndex = -1;
  let searchInput: HTMLInputElement;
  let questionsList: HTMLUListElement;
  let questionItems: HTMLLIElement[] = [];
  
  // Add state to track manual scrolling
  let isManuallyScrolling = false;
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  
  // Set up scroll handlers for the questions list
  function setupScrollHandlers() {
    if (browser && questionsList) {
      // Add scroll event listener to detect manual scrolling
      questionsList.addEventListener('scroll', () => {
        isManuallyScrolling = true;
        
        // Reset the manual scrolling flag after a delay
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          isManuallyScrolling = false;
        }, 1000); // Reset after 1 second of no scrolling
      });
    }
  }
  
  // Subscribe to survey store to get preview mode state and current question updates
  onMount(() => {
    unsubscribe = surveyStore.subscribe(state => {
      if (!forceVisible) {
        previewMode = !!state?.previewMode;
      }
      
      // If the store's current index has changed, update our local copy
      // This helps us detect skips triggered by the survey controller
      if (state?.currentQuestionIndex !== undefined) {
        const storeIndex = state.currentQuestionIndex;
        
        // Only update if there's an actual change
        if (storeIndex !== currentIndex) {
          currentIndex = storeIndex;
          
          // Scroll to the active question after a short delay
          // to ensure DOM has updated, but only if not manually scrolling
          setTimeout(() => {
            if (isOpen && previewMode && !isManuallyScrolling) {
              scrollToActiveQuestion(true); // Force scroll on navigation/skip
            }
          }, 150);
        }
      }
    });
    
    // Check screen size and set up scroll handlers
    checkScreenSize();
    setTimeout(() => setupScrollHandlers(), 100);
    
    // Check if there's a hash parameter for preview mode
    if (browser && window.location.hash && window.location.hash.startsWith('#q')) {
      
      // If we're at the root with a hash navigation marker, make sure preview mode is enabled
      if (window.location.pathname === '/' && window.location.search.includes('preview=true')) {
        // Set preview mode in the store
        surveyStore.setPreviewMode(true);
        
        // Extract the question index from the hash
        const questionIndex = parseInt(window.location.hash.replace('#q', '')) || 0;
        
        // If we're not already at this question, navigate to it
        if (questionIndex !== currentIndex) {
          // Use a short timeout to ensure the preview mode is set first
          setTimeout(() => {
            navigateToQuestion(questionIndex);
          }, 50);
        }
      }
    }
    
    // Add resize listener
    if (browser) {
      window.addEventListener('resize', checkScreenSize);
      window.addEventListener('keydown', handleKeydown);
      document.addEventListener('click', handleClickOutside);
    }
  });
  
  onDestroy(() => {
    if (unsubscribe) unsubscribe();
    
    // Remove listeners
    if (browser) {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('click', handleClickOutside);
    }
  });
  
  // Check screen size
  function checkScreenSize() {
    if (browser) {
      isSmallScreen = window.innerWidth < 1024;
      
      // Automatically open navigator on large screens
      if (!isSmallScreen) {
        isOpen = true;
      }
    }
  }
  
  // Toggle the navigator open/closed
  function toggleNavigator() {
    const wasOpen = isOpen;
    isOpen = !isOpen;
    
    // If opening, focus the search input and notify parent
    if (isOpen && searchInput) {
      setTimeout(() => {
        searchInput.focus();
      }, 100);
      
      // Notify parent that this navigator is opening
      dispatch('navigatorOpened', { navigator: 'question' });
    }
  }

  // Close navigator (called by parent)
  export function closeNavigator() {
    isOpen = false;
  }

  // Panel reference for click outside
  let questionsContainer: HTMLElement;

  // Click outside handler
  function handleClickOutside(event: MouseEvent) {
    if (isOpen && isSmallScreen && questionsContainer && !questionsContainer.contains(event.target as Node)) {
      const toggleButton = document.querySelector('.question-navigator .toggle-button');
      if (toggleButton && !toggleButton.contains(event.target as Node)) {
        isOpen = false;
      }
    }
  }
  
  // Navigate to a specific question
  function navigateToQuestion(index: number | null | undefined) {
    if (!browser) return;
    
    // Update current index locally immediately to help with UI feedback
    if (index !== null && index !== undefined) {
      currentIndex = index;
    }

    // If we have a routePrefix (like '/stats/charts'), use custom routing
    if (routePrefix) {
      // Use routes module for stats charts routing
      const path = routePrefix === '/stats/charts' 
        ? routes.stats.chart(index || 0)
        : `${routePrefix}/${index || 0}`;
      goto(path, { 
        replaceState: false,
        invalidateAll: true
      });
      return;
    }
    
    // Original routing logic for regular survey navigation
    // Special handling for q0/root path
    if (index === 0 || index === null || index === undefined) {
      // When navigating to q0 or root, we need a special approach to maintain preview mode
      // This is because SPA navigation might lose the query parameters
      
      // First, ensure preview mode is set in the store
      surveyStore.setPreviewMode(true);
      
      // For SPA navigation to root with preview parameter preserved
      goto(routes.app.home() + '?preview=true', { 
        replaceState: false,
        invalidateAll: true
      });
      
      return;
    }
    
    // For other questions, prepare the URL with query parameters
    const isPreview = queryParamService.isQueryParamTrue('preview') || ($surveyStore && $surveyStore.previewMode);
    const path = withPreviewMode(routes.survey.question(index), isPreview);
    
    // Use goto for SPA navigation which is more reliable
    goto(path, { 
      replaceState: false,
      invalidateAll: true
    }).then(() => {
      // After navigation completes, scroll to active question
      // Use a longer timeout to allow for any skips to be processed
      setTimeout(() => {
        if (isOpen) {
          scrollToActiveQuestion(true); // Force scroll after navigation
        }
      }, 300);
    });
  }
  
  // Handle item focus events - improve to handle skips
  function handleItemFocus(index: number) {
    selectedResultIndex = index;
    
    // Auto-navigate when an item gets focus
    if (selectedResultIndex >= 0 && selectedResultIndex < filteredScreens.length) {
      const screen = filteredScreens[selectedResultIndex];
      const targetIndex = survey?.screens?.indexOf(screen) ?? 0;
      
      // Only navigate if it's a different question than the current one
      if (targetIndex !== currentIndex) {
        navigateToQuestion(targetIndex);
        // Don't attempt to scroll here, as we'll get a callback from the store
        // after the navigation completes
      }
    }
  }
  
  // Focus the currently selected item and navigate to it
  function focusSelectedItem() {
    if (selectedResultIndex >= 0 && questionItems[selectedResultIndex]) {
      questionItems[selectedResultIndex].focus();
      questionItems[selectedResultIndex].scrollIntoView({ block: 'nearest' });
      
      // Also navigate to the question immediately
      const screen = filteredScreens[selectedResultIndex];
      const targetIndex = survey?.screens?.indexOf(screen) ?? 0;
      
      // Only navigate if it's a different question than the current one
      if (targetIndex !== currentIndex) {
        navigateToQuestion(targetIndex);
      }
    }
  }
  
  // Handle keyboard navigation
  function handleKeydown(event: KeyboardEvent) {
    if (!isOpen || !filteredScreens.length) return;
    
    // Only process if we're focused within the navigator
    const activeElement = document.activeElement;
    const isNavigatorActive = 
      searchInput === activeElement || 
      questionsList?.contains(activeElement) || 
      questionItems.some(item => item === activeElement);
    
    if (!isNavigatorActive && !selectedResultIndex) return;
    
    // Make sure questionItems is up to date
    if (questionItems.length === 0) {
      questionItems = Array.from(questionsList?.querySelectorAll('.question-item:not(.empty)') || []) as HTMLLIElement[];
    }
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        
        // If no item is selected, start from the current question
        if (selectedResultIndex === -1) {
          // Find the index of the current question in the filtered screens list
          const currentScreenIndex = filteredScreens.findIndex(
            (screen: SurveyScreen) => survey?.screens?.indexOf(screen) === currentIndex
          );
          
          // If found, select the next item, otherwise start from the beginning
          if (currentScreenIndex >= 0 && currentScreenIndex < filteredScreens.length - 1) {
            selectedResultIndex = currentScreenIndex + 1;
          } else {
            selectedResultIndex = 0;
          }
          
          focusSelectedItem();
        } else if (selectedResultIndex < filteredScreens.length - 1) {
          selectedResultIndex++;
          focusSelectedItem();
        }
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        
        // If no item is selected, start from the current question
        if (selectedResultIndex === -1) {
          // Find the index of the current question in the filtered screens list
          const currentScreenIndex = filteredScreens.findIndex(
            (screen: SurveyScreen) => survey?.screens?.indexOf(screen) === currentIndex
          );
          
          // If found and not at the top, select the previous item, otherwise go to the last item
          if (currentScreenIndex > 0) {
            selectedResultIndex = currentScreenIndex - 1;
            focusSelectedItem();
          } else if (filteredScreens.length > 0) {
            // If at first item or not found, go to the last item
            selectedResultIndex = filteredScreens.length - 1;
            focusSelectedItem();
          }
        } else if (selectedResultIndex > 0) {
          selectedResultIndex--;
          focusSelectedItem();
        } else if (selectedResultIndex === 0) {
          // Focus back to search input when at the top
          selectedResultIndex = -1;
          searchInput?.focus();
        }
        break;
        
      case 'Escape':
        event.preventDefault();
        if (searchQuery) {
          // Clear search if there's a query
          searchQuery = '';
          selectedResultIndex = -1;
          searchInput?.focus();
        }
        break;
    }
  }
  
  // Use centralized display name function
  function getScreenDisplayName(screen: SurveyScreen | undefined | null, index: number): string {
    return screen?.questionId || `q${index}`;
  }
  
  // Get question content (title, blocks content, subtitle, or alt text) - UPDATED LOGIC
  function getQuestionContent(screen: SurveyScreen | undefined | null): string {
    if (!screen || !screen.header) return '';

    // 1. Prioritize header.title
    if (screen.header.title && typeof screen.header.title === 'string' && screen.header.title.trim() !== '') {
      return screen.header.title.trim();
    }

    // 2. Look for the first text block in header.blocks
    if (Array.isArray(screen.header.blocks)) {
      // @ts-expect-error - header.blocks might not be strictly typed here yet
      const textBlock = screen.header.blocks.find(block => block.type === 'text' && block.content);
      if (textBlock && typeof textBlock.content === 'string' && textBlock.content.trim() !== '') {
        // Strip markdown for display in navigator (simple regex for common elements)
        return textBlock.content.replace(/\*\*|__|## |# |`|\*|_/g, '').trim();
      }
    }

    // 3. Fallback to subtitle
    if (screen.header.subtitle && typeof screen.header.subtitle === 'string' && screen.header.subtitle.trim() !== '') {
      return screen.header.subtitle.trim();
    }

    // 4. Fallback to image alt
    if (screen.header.image?.alt && typeof screen.header.image.alt === 'string' && screen.header.image.alt.trim() !== '') {
      return screen.header.image.alt.trim();
    }

    return ''; // Default empty
  }
  
  // Get screen type display
  function getScreenType(screen: SurveyScreen | undefined | null): string {
    if (!screen) return 'Unknown';
    if (screen.type === 'screen') return 'Screen';
    if (screen.type === 'question') return screen.questionType || 'Question';
    if (screen.type === 'profile') return 'Profile';
    return screen.type || 'Unknown';
  }
  
  // Check if any option keys or values match the search query
  function optionsMatch(screen: SurveyScreen | undefined | null, query: string): boolean {
    if (!screen || !screen.options) return false;
    
    // Check items (options)
    if (screen.options.items && Array.isArray(screen.options.items)) {
      for (const item of screen.options.items) {
        if (
          (item.key && String(item.key).toLowerCase().includes(query)) ||
          (item.label && String(item.label).toLowerCase().includes(query))
        ) {
          return true;
        }
      }
    }
    
    // Check altItems (alternative options)
    if (screen.options.altItems && Array.isArray(screen.options.altItems)) {
      for (const item of screen.options.altItems) {
        if (
          (item.key && String(item.key).toLowerCase().includes(query)) ||
          (item.label && String(item.label).toLowerCase().includes(query))
        ) {
          return true;
        }
      }
    }
    
    // Check altResponse
    if (screen.options.altResponse) {
      const altResponse = screen.options.altResponse;
      if (
        (altResponse.key && String(altResponse.key).toLowerCase().includes(query)) ||
        (altResponse.label && String(altResponse.label).toLowerCase().includes(query))
      ) {
        return true;
      }
    }
    
    return false;
  }
  
  // Reset selectedResultIndex when filtered items change
  $: {
    filteredScreens;
    selectedResultIndex = -1;
  }
  
  // Filter questions based on search query and filtered question IDs, then deduplicate by questionId
  $: filteredScreens = (() => {
    if (!survey?.screens) return [];
    
    // Step 1: Apply initial filters
    const initialFiltered = survey.screens.filter((screen: SurveyScreen) => {
      // Only hide questions marked as hidden if we're in stats/dashboard mode (not preview mode)
      if ((screen.data?.hidden === true || screen.hidden === true) && routePrefix.includes('/stats')) {
        return false; // Hide questions marked as hidden only in dashboard
      }
      
      // Then check if we have filtered question IDs and if this screen should be included
      if (filteredQuestionIds.length > 0) {
        const screenQuestionId = screen.questionId || screen.id;
        if (!filteredQuestionIds.includes(screenQuestionId)) {
          return false; // Hide this question if it's not in the filtered list
        }
      }
      
      // Then apply search query filter
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      const id = `q${survey.screens.indexOf(screen)}`.toLowerCase();
      const name = getScreenDisplayName(screen, survey.screens.indexOf(screen)).toLowerCase();
      const content = getQuestionContent(screen).toLowerCase();
      const type = getScreenType(screen).toLowerCase();
      
      // Add option keys and values to the search
      const optionsMatched = optionsMatch(screen, query);
      
      return id.includes(query) || 
             name.includes(query) || 
             content.includes(query) || 
             type.includes(query) ||
             optionsMatched;
    });
    
    // Step 2: Apply deduplication only in stats mode, not in preview mode
    let finalFiltered = initialFiltered;
    if (routePrefix.includes('/stats')) {
      // Only deduplicate when in stats dashboard view
      finalFiltered = deduplicateScreens(initialFiltered);
    }
    
    return finalFiltered;
  })();
  
  // React to changes in the currentIndex prop (which can happen due to skips)
  $: if (currentIndex !== lastKnownIndex) {
    lastKnownIndex = currentIndex;
    // Use setTimeout to ensure DOM is updated before scrolling
    setTimeout(() => {
      if (isOpen) {
        // Force scroll on explicit index changes, like skips
        scrollToActiveQuestion(true);
        // Also update the questionItems array to reflect any changes
        questionItems = Array.from(questionsList?.querySelectorAll('.question-item:not(.empty)') || []) as HTMLLIElement[];
      }
    }, 200);
  }
  
  // Scroll the active question into view
  function scrollToActiveQuestion(forceScroll = false) {
    if (!isOpen || !questionsList) return;
    
    // Don't scroll if user is manually scrolling, unless force scroll is requested
    if (isManuallyScrolling && !forceScroll) return;
    
    // Find the currently active question item in the DOM
    const activeItem = questionsList.querySelector('.question-item.active');
    if (activeItem) {
     
      // Check if the active item is already visible in the viewport
      const listRect = questionsList.getBoundingClientRect();
      const activeRect = activeItem.getBoundingClientRect();
      
      // Only scroll if item is not fully visible in the container
      const isVisible = (
        activeRect.top >= listRect.top &&
        activeRect.bottom <= listRect.bottom
      );
      
      if (!isVisible || forceScroll) {
        activeItem.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
        
        // For better visibility, highlight the active item briefly
        activeItem.classList.add('highlight-pulse');
        setTimeout(() => {
          activeItem.classList.remove('highlight-pulse');
        }, 1500);
      }
    }
  }
  
  // Reset questionItems array when necessary, but handle it in onMount and after DOM updates
  $: if (filteredScreens) {
    // Schedule this after the DOM has updated to ensure questionItems can be properly referenced
    setTimeout(() => {
      // Refresh the questionItems array to match the current DOM state
      // Use a more specific selector to exclude the empty items
      questionItems = Array.from(questionsList?.querySelectorAll('.question-item:not(.empty)') || []) as HTMLLIElement[];
      
      // After updating, scroll to the active question if needed,
      // but don't force it if user is manually scrolling
      if (isOpen && !isManuallyScrolling) {
        scrollToActiveQuestion(false); // Don't force scroll when refreshing list
      }
      
    }, 0);
  }
  
  // Hide the navigator if not in preview mode and not force visible
  $: if (!forceVisible && $surveyStore && $surveyStore.previewMode === false) {
    isOpen = false;
  }
</script>

{#if showNavigator}
  <div class="question-navigator" class:open={isOpen} class:small-screen={isSmallScreen} class:large-screen={!isSmallScreen} class:force-hidden={hidden}>
    <button
      class="toggle-button"
      on:click={toggleNavigator}
      aria-label={isOpen ? currentTranslations.close_question_navigator : currentTranslations.open_question_navigator}
    >
      {#if isOpen}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      {:else}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <path d="M13 8h-6"></path>
          <path d="M13 12h-6"></path>
        </svg>
      {/if}
    </button>
    
    {#if isOpen}
      <div class="questions-container" bind:this={questionsContainer}>
        <div class="header">
          <div class="header-top">
            <h3>{currentTranslations.question_navigator}</h3>
            <button 
              class="home-button" 
              on:click={() => {
                if (routePrefix.includes('/stats')) {
                  goto(routes.stats.index());
                } else {
                  navigateToQuestion(0);
                }
              }}
              aria-label="Navigate to stats dashboard"
              title="Go to stats dashboard"
            >
              ↖
            </button>
          </div>
          
          <div class="search-box">
            <input 
              type="text" 
              placeholder={currentTranslations.search_questions_placeholder} 
              bind:value={searchQuery}
              bind:this={searchInput}
              aria-label={currentTranslations.search_questions}
            />
            {#if searchQuery}
              <button 
                class="clear-search" 
                on:click={() => {
                  searchQuery = '';
                  searchInput.focus();
                }} 
                aria-label={currentTranslations.clear_search}
              >
                ×
              </button>
            {/if}
          </div>
          
          <div class="keyboard-hint" class:hidden={!filteredScreens.length}>
            <span>{currentTranslations.use_arrows_to_navigate}</span>
          </div>
        </div>
        
        <ul class="questions-list" bind:this={questionsList}>
          {#if filteredScreens && filteredScreens.length > 0}
            {#each filteredScreens as screen, idx}
              {@const index = survey?.screens?.indexOf(screen) ?? 0}
              <li 
                class="question-item" 
                class:active={index === currentIndex}
                class:selected={idx === selectedResultIndex}
                on:click={() => navigateToQuestion(index)}
                on:focus={() => handleItemFocus(idx)}
                tabindex="0"
                bind:this={questionItems[idx]}
              >
                <span class="question-index">q{index}</span>
                <div class="question-details">
                  <span class="question-name">{getScreenDisplayName(screen, index)}</span>
                  <span class="question-content">{getQuestionContent(screen)}</span>
                  <span class="question-type">{getScreenType(screen)}</span>
                </div>
              </li>
            {/each}
          {:else if searchQuery}
            <li class="question-item empty">
              <span>{currentTranslations.no_matches_found} "{searchQuery}"</span>
            </li>
          {:else}
            <li class="question-item empty">
              <span>{currentTranslations.no_questions_available}</span>
            </li>
          {/if}
        </ul>
      </div>
    {/if}
  </div>
{/if}

<style>
  .question-navigator {
    position: fixed;
    z-index: 1000;
    font-family: var(--theme-font-family, system-ui, sans-serif);
    user-select: none;
  }
  
  /* Apply theme font to all elements */
  .question-navigator * {
    font-family: var(--theme-font-family, system-ui, sans-serif);
  }
  
  /* Small screen styles (mobile, tablet) */
  .question-navigator.small-screen {
    top: 5px;
    left: 5px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  
  /* Large screen styles (laptop and above) */
  .question-navigator.large-screen {
    top: 0;
    left: 0;
    height: 100vh;
    display: flex;
  }
  
  .large-screen .questions-container {
    height: 100vh;
    width: 320px;
    border-radius: 0;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }
  
  .large-screen .toggle-button {
    display: none;
  }
  
  .toggle-button {
    width: 36px;
    height: 36px;
    border-radius: var(--radius, 5px);
    background: var(--theme-primary-color, #10b981);
    color: var(--theme-text-on-primary, white);
    border: none;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }
  
  .toggle-button:hover {
    transform: scale(1.05);
  }

  .toggle-button svg {
    pointer-events: none;
  }

  .small-screen .questions-container {
    margin-top: 10px;
    width: 300px;
    max-height: 70vh;
    animation: slideIn 0.2s ease;
  }
  
  .questions-container {
    background: var(--theme-bg-color, white);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow-y: auto;
  }
  
  .header {
    padding: 15px;
    border-bottom: 1px solid var(--theme-border-color, #eaeaea);
    position: sticky;
    top: 0;
    background: var(--theme-bg-color, white);
    z-index: 2;
  }
  
  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .home-button {
    background: var(--theme-primary-color, #10b981);
    color: var(--theme-text-on-primary, white);
    border: none;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .home-button:hover {
    transform: scale(1.05);
    opacity: 0.9;
  }
  
  .header h3 {
    margin: 0;
    color: var(--theme-text-color, #1f2937);
    font-size: 16px;
    font-weight: 600;
  }
  
  .subtitle {
    margin: 4px 0 10px;
    font-size: 12px;
    color: var(--theme-secondary-color, #6b7280);
  }
  
  .search-box {
    position: relative;
    margin-top: 10px;
  }
  
  .keyboard-hint {
    font-size: 11px;
    color: var(--theme-secondary-color, #6b7280);
    margin-top: 8px;
    text-align: center;
  }
  
  .keyboard-hint.hidden {
    display: none;
  }
  
  .search-box input {
    width: calc(100% - 24px);
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid var(--theme-border-color, #e5e7eb);
    font-size: 14px;
    background: var(--theme-bg-color, #f9fafb);
    color: var(--theme-text-color, #1f2937);
  }
  
  .search-box input:focus {
    outline: none;
    border-color: var(--theme-primary-color, #10b981);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-primary-color, #10b981) 20%, transparent);
  }
  
  .clear-search {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--theme-secondary-color, #9ca3af);
    cursor: pointer;
    font-size: 16px;
    padding: 4px;
    line-height: 1;
  }
  
  .questions-list {
    list-style: none;
    padding: 0;
    margin: 0;
    overflow-y: auto;
  }
  
  .question-item {
    display: flex;
    align-items: flex-start;
    padding: 12px 15px;
    border-bottom: 1px solid var(--theme-border-color, #f0f0f0);
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
  }
  
  .question-item:focus {
    background-color: color-mix(in srgb, var(--theme-primary-color, #10b981) 5%, transparent);
    box-shadow: 0 0 0 2px var(--theme-primary-color, #10b981) inset;
  }
  
  .question-item.empty {
    justify-content: center;
    color: var(--theme-secondary-color, #6b7280);
    font-size: 14px;
    padding: 20px 15px;
    cursor: default;
  }
  
  .question-item:hover:not(.empty) {
    background-color: color-mix(in srgb, var(--theme-bg-color, #f8f9fa) 50%, var(--theme-border-color, #f0f0f0) 50%);
  }
  
    .question-item.active {
    background-color: color-mix(in srgb, var(--theme-primary-color, #10b981) 10%, transparent);
    border-left: 3px solid var(--theme-primary-color, #10b981);
  }

  .question-item.selected {
    background-color: color-mix(in srgb, var(--theme-primary-color, #10b981) 15%, transparent);
  }
  
  .question-index {
    width: 35px;
    height: 35px;
    background-color: var(--theme-border-color, #f0f0f0);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    margin-right: 12px;
    color: var(--theme-text-color, #555);
    font-weight: 600;
    flex-shrink: 0;
  }
  
  .active .question-index {
    background-color: var(--theme-primary-color, #10b981);
    color: white;
  }
  
  .question-details {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }
  
  .question-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--theme-text-color, #1f2937);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .question-content {
    font-size: 13px;
    color: var(--theme-text-color, #1f2937);
    margin: 3px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  
  .question-type {
    font-size: 11px;
    color: var(--theme-secondary-color, #6b7280);
    margin-top: 2px;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Dark mode support */
  :global([data-theme='dark']) .questions-container,
  :global([data-theme='dark']) .header {
    background-color: var(--theme-bg-color-dark, #1f2937);
    border-color: var(--theme-border-color-dark, #374151);
  }
  
  :global([data-theme='dark']) .search-box input {
    background-color: #2d3748;
    border-color: #4b5563;
    color: var(--theme-text-color-dark, #f3f4f6);
  }
  
  :global([data-theme='dark']) .clear-search {
    color: #9ca3af;
  }
  
  :global([data-theme='dark']) .header h3 {
    color: var(--theme-text-color-dark, #f3f4f6);
  }
  
  :global([data-theme='dark']) .subtitle,
  :global([data-theme='dark']) .keyboard-hint {
    color: var(--theme-secondary-color, #9ca3af);
  }
  
  :global([data-theme='dark']) .question-item {
    border-bottom-color: var(--theme-border-color-dark, #374151);
  }
  
  :global([data-theme='dark']) .question-item:hover:not(.empty) {
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  :global([data-theme='dark']) .question-item:focus {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  :global([data-theme='dark']) .question-item.empty {
    color: var(--theme-secondary-color, #9ca3af);
  }
  
  :global([data-theme='dark']) .question-index {
    background-color: #2d3748;
    color: #e2e8f0;
  }
  
  :global([data-theme='dark']) .question-name,
  :global([data-theme='dark']) .question-content {
    color: var(--theme-text-color-dark, #f3f4f6);
  }
  
  :global([data-theme='dark']) .question-type {
    color: var(--theme-secondary-color, #9ca3af);
  }
  
  :global([data-theme='dark']) .home-button {
    background: var(--theme-primary-color, #10b981);
    color: var(--theme-text-on-primary, white);
  }
  
  /* Add styles for the highlight pulse animation */
  @keyframes highlightPulse {
    0% { background-color: color-mix(in srgb, var(--theme-primary-color, #10b981) 10%, transparent); }
    50% { background-color: color-mix(in srgb, var(--theme-primary-color, #10b981) 30%, transparent); }
    100% { background-color: color-mix(in srgb, var(--theme-primary-color, #10b981) 10%, transparent); }
  }
  
  .highlight-pulse {
    animation: highlightPulse 1.5s ease;
  }

  /* Force hidden when master toggle is activated */
  .question-navigator.force-hidden {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
</style> 